import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";

import Checkbox from "antd/es/checkbox";
import Divider from "antd/es/divider";
import Card from "antd/es/card";

import { Row, Col } from "components/layout";
import { Text } from "components/Text";
import SearchInput from "components/Search";

import trackingMapConfig from "config/trackingMap";

import useWebSocket from "hooks/useWebSocket";
import useQueryParam from "hooks/useQueryParam";

import MonitorService from "services/monitor";

import TrackingMap from "../components/TrackingMap";
import Statistics from "components/Statistics";

import { categoryToType, colorMap } from "../constants";
import {
  applyFilters,
  mapDataToDoorInfos,
  mapDataToGatewayInfos,
  mapMessagesToDeviceInfos,
  reduceTagsToKeyValue,
} from "../helper";
import RadioGroup from "components/RadioGroup";
import useCartsHeatmap from "../hooks/useCartsHeatmap";
// import InfoLabel from "components/InfoLabel";

const TackingTab = () => {
  const [showUpdates] = useQueryParam("debug", false);
  const [isLoading, setIsLoading] = useState(true);
  const [connecting, setConnecting] = useState(true);
  const monitorService = useMemo(() => new MonitorService(), []);
  const [mapConfig, setMapConfig] = useState({});
  const [mapInfo, setMapInfo] = useState({ doors: [], gateways: [] });
  const [trackingInfo, setTrackingInfo] = useState({
    carts: {},
    forklifts: {},
    employees: {},
  });
  const [currentTrackingMessages, setCurrentTrackingMessages] = useState([]);
  const [visibleTags, setVisibleTags] = useState(Object.values(categoryToType));
  const [searchQuery, setSearchQuery] = useState("");
  const [showLabels, setShowLabels] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [heatmapDecimal, setHeatmapDecimal] = useState(10);
  const [focusedCart, setFocusedCart] = useState(null);

  const { cart_color, person_color } = mapConfig;
  const cartColor = colorMap[cart_color] || cart_color || colorMap.black;
  // const forkliftColor = colorMap[forklift_color] || forklift_color || colorMap.black;;
  const employeeColor =
    colorMap[person_color] || person_color || colorMap.black;

  const carts = useMemo(() => {
    return applyFilters(trackingInfo.carts, visibleTags, searchQuery);
  }, [trackingInfo, searchQuery, visibleTags]);

  const forklifts = useMemo(() => {
    return applyFilters(trackingInfo.forklifts, visibleTags, searchQuery);
  }, [trackingInfo.forklifts, searchQuery, visibleTags]);

  const employees = useMemo(() => {
    return applyFilters(trackingInfo.employees, visibleTags, searchQuery);
  }, [trackingInfo, searchQuery, visibleTags]);

  useEffect(() => {
    if (carts.length === 1) {
      setFocusedCart(carts[0]);
    } else {
      setFocusedCart(null);
      setShowHeatmap(false);
    }
  }, [carts]);

  const cartsHeatmap = useCartsHeatmap({ selectedCart: focusedCart, currentTrackingMessages, heatmapDecimal });

  useEffect(() => {
    setIsLoading(true);
    monitorService
      .getDCMap()
      .then((data) => {
        if (!data) return;
        const mapConfig = data.location.map_config;
        setMapConfig(mapConfig);
        setMapInfo({
          doors: mapDataToDoorInfos(data.doors),
          gateways: mapDataToGatewayInfos(data.gateways),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [monitorService]);

  const { connected } = useWebSocket({
    url: trackingMapConfig.wsUrl,
    onMessage: (messages) => {
      if (!messages || !messages.length) return;
      setConnecting(false);

      const trackingMessages = mapMessagesToDeviceInfos(messages).filter(
        (deviceInfo) => deviceInfo.isActive
      );

      const carts = reduceTagsToKeyValue(
        trackingMessages.filter((d) => d.type === "cartTag"),
        trackingInfo.carts
      );

      const employees = reduceTagsToKeyValue(
        trackingMessages.filter((d) => d.type === "personTag"),
        trackingInfo.employees
      );

      const forklifts = reduceTagsToKeyValue(
        trackingMessages.filter((d) => d.type === "forkliftTag"),
        trackingInfo.forklifts
      );

      setTrackingInfo({
        carts,
        employees,
        forklifts,
      });

      setCurrentTrackingMessages(trackingMessages);
    },
  });

  useEffect(() => {
    if (connected) {
      console.log("WS Connected");
    } else {
      console.log("WS Disconnected");
    }
  }, [connected]);

  if (isLoading) return null;

  return (
    <Row style={{ padding: "24px" }}>
      <Col span={12}>
        <Row>
          <Col>
            <InfoSection title="Overview">
              <InfoSectionStatistic
                title="Individuals"
                titleAlign={["left", "bottom"]}
                value={Object.values(trackingInfo.employees).length}
                style={{
                  alignItems: "flex-start",
                }}
              />
              <InfoSectionStatistic
                title="Carts"
                titleAlign={["left", "bottom"]}
                value={Object.values(trackingInfo.carts).length}
                style={{
                  alignItems: "flex-start",
                }}
              />
            </InfoSection>
            <InfoSection title="Legend">
              <Legend>
                <LegendItem
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="8" cy="8" r="8" fill={employeeColor} />
                    </svg>
                  }
                  label="Individuals"
                />
                <LegendItem
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 0.5L16 16H0.138784L7.5 0.5Z"
                        fill={cartColor}
                      />
                    </svg>
                  }
                  label="Carts"
                />
                {/* <LegendItem
                  icon={(
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="16" height="16" fill={forkliftColor} />
                    </svg>
                  )}
                  label="Forklifts"
                /> */}
              </Legend>
            </InfoSection>
          </Col>
          <Col span={6} style={{ display: "flex", justifyContent: "center" }}>
            <Divider type="vertical" style={{ height: "85%" }} />
          </Col>
          <Col>
            <InfoSection title="Filters">
              <CategoryFilters>
                <CategoryCheckbox
                  checked={visibleTags.includes("personTag")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleTags((tags) => [...tags, "personTag"]);
                    } else {
                      setVisibleTags((tags) =>
                        tags.filter((t) => t !== "personTag")
                      );
                    }
                  }}
                >
                  Individuals
                </CategoryCheckbox>
                <CategoryCheckbox
                  checked={visibleTags.includes("cartTag")}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleTags((tags) => [...tags, "cartTag"]);
                    } else {
                      setVisibleTags((tags) =>
                        tags.filter((t) => t !== "cartTag")
                      );
                    }
                  }}
                >
                  Cart
                </CategoryCheckbox>
                {/* <CategoryCheckbox
                  checked={visibleTags.includes('forkliftTag')}
                  onChange={e => {
                    if (e.target.checked) {
                      setVisibleTags(tags => [...tags, 'forkliftTag']);
                    } else {
                      setVisibleTags(tags => tags.filter(t => t !== 'forkliftTag'))
                    }
                  }}
                >
                  Forklifts
                </CategoryCheckbox> */}
              </CategoryFilters>
              <SearchInputWrapper>
                <SearchInput
                  value={searchQuery}
                  onChange={(value) => {
                    setSearchQuery(value);
                  }}
                  style={{ height: "38px", maxWidth: "180px" }}
                  allowClear
                />
              </SearchInputWrapper>
              <ShowCheckbox
                checked={showLabels}
                onChange={(e) => {
                  setShowLabels(e.target.checked);
                }}
                style={{
                  marginTop: "24px",
                }}
              >
                Show Labels
              </ShowCheckbox>
            </InfoSection>
            {focusedCart ? (
              <InfoSection title="Heatmap">
                <ShowCheckbox
                  checked={showHeatmap}
                  onChange={(e) => {
                    setShowHeatmap(e.target.checked);
                  }}
                  style={{
                    marginTop: "8px",
                  }}
                >
                  Show Heatmap
                </ShowCheckbox>
                {showHeatmap ? (
                  <>
                    <Text
                      style={{
                        color: "#000",
                        fontWeight: 400,
                        fontSize: "16px",
                      }}
                    >
                      {"Scale "}
                    </Text>
                    <RadioGroup
                      options={[
                        { value: 1, label: "100cm" },
                        { value: 10, label: "10cm" },
                        { value: 100, label: "1cm" },
                      ]}
                      optionType={"button"}
                      onChange={(e) => {
                        setHeatmapDecimal(e.target.value);
                      }}
                      value={heatmapDecimal}
                    />
                  </>
                ) : null}
              </InfoSection>
            ) : null}
          </Col>
        </Row>
        {!!showUpdates ? (
          <Row style={{ paddingTop: "48px" }}>
            <TrackingUpdates trackingInfo={trackingInfo} />
          </Row>
        ) : (
          false
        )}
      </Col>
      <Col span={12}>
        <TrackingMap
          showLabels={showLabels}
          mapConfig={mapConfig}
          doors={mapInfo.doors}
          gateways={mapInfo.gateways}
          carts={carts}
          forklifts={forklifts}
          employees={employees}
          showHeatmap={showHeatmap}
          cartsHeatmap={cartsHeatmap}
          connecting={connecting}
        />
      </Col>
    </Row>
  );
};

const InfoSection = ({ title, children }) => {
  return (
    <InfoSectionWrapper>
      <Text className="title">{title}</Text>
      <div className="content">{children}</div>
    </InfoSectionWrapper>
  );
};

const LegendItem = ({ icon, label }) => {
  return (
    <LegendItemWrapper>
      <span className="icon">{icon}</span>
      <Text className="label">{label}</Text>
    </LegendItemWrapper>
  );
};

const TrackingUpdates = ({ trackingInfo }) => {
  const updates = [
    ...Object.values(trackingInfo.carts),
    ...Object.values(trackingInfo.employees),
    ...Object.values(trackingInfo.forklifts),
  ];

  return (
    <Card size="small" title="Tracking Updates" style={{ width: "100%" }}>
      <Row justify="center">
        {updates.length ? (
          <Col span={24}>
            {updates.map((item) => (
              <Text>
                {[
                  `Device(${item.type}) MAC: ${item.id}`,
                  `Name: ${item.name}`,
                  `x: ${item.x}`,
                  `y: ${item.y}`,
                ].join(", ")}
              </Text>
            ))}
          </Col>
        ) : (
          <Col>
            <Text color="grey">No updates</Text>
          </Col>
        )}
      </Row>
    </Card>
  );
};

const InfoSectionWrapper = styled.div`
  & .title {
    font-weight: 500;
    font-size: 12px;
    line-height: 20px;
    color: #828c94;
    text-transform: uppercase;
    padding-bottom: 8px;
    display: inline-block;
  }

  & .content {
    & > *:not(:last-child) {
      padding-bottom: 12px;
    }
  }

  &:not(:last-child) {
    padding-bottom: 32px;
  }
`;

const InfoSectionStatistic = styled(Statistics)`
  & .ant-statistic-title {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #232526;
  }

  & .ant-statistic-content-value {
    font-weight: 600;
    font-size: 30px;
    line-height: 38px;
    color: #000000;
  }
`;

const Legend = styled.div`
  & .icon {
    display: inline-flex;
    padding-right: 8px;
  }

  & .label {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #232526;
  }
`;

const LegendItemWrapper = styled.div`
  display: flex;
  align-items: center;

  &:not(:last-child) {
    padding-bottom: 8px;
  }
`;

const CategoryFilters = styled.div`
  & > *:not(:last-child) {
    padding-bottom: 8px;
  }
`;

const CategoryCheckbox = styled(Checkbox)`
  display: block;

  & > span:not(.ant-checkbox) {
    color: #232526;
  }

  & + .ant-checkbox-wrapper {
    margin-left: unset;
  }
`;

const ShowCheckbox = styled(Checkbox)`
  display: block;

  & > span:not(.ant-checkbox) {
    color: #232526;
  }

  & + .ant-checkbox-wrapper {
    margin-left: unset;
  }
`;

const SearchInputWrapper = styled.div`
  margin-top: 8px;
`;

export default TackingTab;
